import React, { useEffect, useRef, useCallback, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, User, Volume2, Mic, Eye, EyeOff } from 'lucide-react';

// Message interface matching the existing pattern
interface ConversationMessage {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: number;
  isComplete?: boolean;
}

interface TranscriptionPanelProps {
  className?: string;
  onClose?: () => void;
  aiName?: string;
  userName?: string;
  colorScheme?: 'default' | 'black';
  aiLogoSrc?: string;
  messages?: ConversationMessage[];
  isAiSpeaking?: boolean;
  isUserSpeaking?: boolean;
  isMuted?: boolean;
}

// Enhanced typewriter effect for smooth streaming
const TypewriterText: React.FC<{
  text: string;
  isComplete?: boolean;
  speed?: number;
}> = ({ text, isComplete = true, speed = 25 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const textRef = useRef('');

  useEffect(() => {
    if (isComplete) {
      setDisplayedText(text);
      setCurrentIndex(text.length);
      textRef.current = text;
      return;
    }

    // Only reset if this is a completely new message (different from what we had)
    if (textRef.current !== text && text.length < textRef.current.length) {
      setCurrentIndex(0);
      setDisplayedText('');
      textRef.current = text;
    }

    // Update our reference
    textRef.current = text;

    // Continue typing if we have more text to show
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(prev => prev + 1);
      }, speed);
      return () => clearTimeout(timer);
    }
  }, [text, currentIndex, isComplete, speed]);

  return (
    <span className="whitespace-pre-wrap text-gray-100">
      {displayedText}
    </span>
  );
};

export const TranscriptionPanel: React.FC<TranscriptionPanelProps> = ({
  className = '',
  onClose,
  aiName = 'MTAI AI',
  userName = 'You',
  colorScheme = 'default',
  aiLogoSrc,
  messages = [],
  isAiSpeaking = false,
  isUserSpeaking = false,
  isMuted = false
}) => {
  // Refs for scrolling
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const autoScrollRef = useRef(true);
  const [isMinimized, setIsMinimized] = useState(false);

  // Mobile drag state - snap positions (in vh units)
  const [panelHeight, setPanelHeight] = useState(40); // 40vh default
  const isMobile = window.innerWidth < 1024;

  // Handle drag end - snap to positions
  const handleDragEnd = useCallback((event: any, info: any) => {
    if (!isMobile) return;

    const velocity = info.velocity.y;
    const offset = info.offset.y;

    // Fast swipe down = close
    if (velocity > 500) {
      if (onClose) onClose();
      return;
    }

    // Fast swipe up = expand to 60vh
    if (velocity < -500) {
      setPanelHeight(60);
      return;
    }

    // Calculate current height based on drag offset
    const currentHeight = panelHeight - (offset / window.innerHeight) * 100;

    // Snap to nearest position: 25vh, 40vh, or 60vh
    if (currentHeight < 32) {
      // Close if dragged below 32vh
      if (onClose) onClose();
    } else if (currentHeight < 50) {
      // Snap to 40vh (medium)
      setPanelHeight(40);
    } else {
      // Snap to 60vh (large)
      setPanelHeight(60);
    }
  }, [isMobile, panelHeight, onClose]);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current && autoScrollRef.current) {
      setTimeout(() => {
        if (scrollAreaRef.current) {
          scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
        }
      }, 50);
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Handle scroll events to detect manual scrolling
  const handleScroll = useCallback(() => {
    if (scrollAreaRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollAreaRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 30;
      autoScrollRef.current = isAtBottom;
    }
  }, []);

  // Format timestamp
  const formatTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <motion.div
      // Mobile: slide up from bottom as overlay (Zoom-style) - DRAGGABLE
      // Desktop: takes flex-1 (60% of space since video is 40%)
      drag={isMobile ? "y" : false}
      dragConstraints={isMobile ? { top: 0, bottom: 200 } : undefined}
      dragElastic={0.2}
      onDragEnd={isMobile ? handleDragEnd : undefined}
      initial={{
        height: isMobile ? '0%' : '100%',
        opacity: isMobile ? 0.5 : 0,
        y: isMobile ? 100 : 0,
        x: isMobile ? 0 : 20
      }}
      animate={{
        height: isMobile ? `${panelHeight}vh` : '100%',
        opacity: 1,
        y: 0,
        x: 0
      }}
      exit={{
        height: isMobile ? '0%' : '100%',
        opacity: isMobile ? 0.5 : 0,
        y: isMobile ? 100 : 0,
        x: isMobile ? 0 : 20
      }}
      transition={{ duration: 0.3, ease: "easeOut", type: "spring", damping: 30, stiffness: 300 }}
      className={`flex flex-col relative ${className}
        lg:w-96 lg:h-full lg:border-l lg:flex-shrink-0
        fixed lg:relative bottom-0 lg:bottom-auto left-0 lg:left-auto right-0 lg:right-auto
        w-full
        rounded-t-3xl lg:rounded-none
        z-50 lg:z-auto
        touch-none lg:touch-auto`}
    >
      {/* Clean glass background */}
      <div className="absolute inset-0 bg-black/30 lg:bg-black/20 backdrop-blur-xl border-l-0 lg:border-l border-white/10 rounded-t-3xl lg:rounded-none" />

      <div className="relative z-10 flex flex-col h-full">
        {/* Mobile drag handle - Interactive with larger touch area */}
        <div className="lg:hidden flex justify-center pt-3 pb-3 cursor-grab active:cursor-grabbing touch-manipulation">
          <div className="w-16 h-1.5 bg-white/40 rounded-full transition-all active:bg-white/60 active:scale-105"></div>
        </div>

        {/* Clean Header */}
        <div className="relative p-4 border-b border-white/5">
          <div className="flex items-center justify-between">
            {!isMinimized ? (
              <>
                <div className="flex items-center space-x-3">
                  <div className="relative p-1">
                    <div className="w-9 h-9 bg-amber-500/20 rounded-2xl flex items-center justify-center border border-amber-400/20">
                      <MessageCircle className="w-5 h-5 text-amber-400" />
                    </div>
                    {(isAiSpeaking || (isUserSpeaking && !isMuted)) && (
                      <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-green-400 rounded-full animate-pulse border-2 border-gray-900">
                        <div className="w-full h-full bg-green-500 rounded-full animate-ping opacity-75" />
                      </div>
                    )}
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-white text-sm">
                      Live Transcription
                    </h3>
                    <div className="flex items-center space-x-2 text-xs mt-0.5">
                      {isAiSpeaking && (
                        <div className="flex items-center space-x-1.5 text-amber-400">
                          <div className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse" />
                          <span className="font-medium">AI Speaking</span>
                        </div>
                      )}
                      {isUserSpeaking && !isMuted && (
                        <div className="flex items-center space-x-1.5 text-green-400">
                          <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                          <span className="font-medium">You're Speaking</span>
                        </div>
                      )}
                      {!isAiSpeaking && !(isUserSpeaking && !isMuted) && (
                        <span className="text-gray-400 font-medium">Ready to listen</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setIsMinimized(true)}
                    className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-200 text-gray-400 hover:text-white"
                    title="Minimize panel"
                  >
                    <EyeOff className="w-4 h-4" />
                  </button>

                  {onClose && (
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-200 text-gray-400 hover:text-white"
                      title="Close panel"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </>
            ) : (
              <button
                onClick={() => setIsMinimized(false)}
                className="w-full flex flex-col items-center justify-center space-y-3 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-200 text-gray-400 hover:text-white group"
                title="Expand panel"
              >
                <div className="relative">
                  <Eye className="w-5 h-5 group-hover:scale-110 transition-transform duration-200" />
                  {(isAiSpeaking || isUserSpeaking) && (
                    <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  )}
                </div>
                <span className="text-xs font-medium transform rotate-90 whitespace-nowrap">
                  Transcription
                </span>
              </button>
            )}
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages Container */}
            <div
              ref={scrollAreaRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto p-4 space-y-6 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/20 hover:scrollbar-thumb-white/30"
              style={{ maxHeight: 'calc(100vh - 200px)' }}
            >
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-center">
                  <div className="w-14 h-14 bg-amber-500/10 rounded-3xl flex items-center justify-center mb-4 border border-amber-400/20">
                    <MessageCircle className="w-7 h-7 text-amber-400/80" />
                  </div>
                  <p className="text-gray-300 text-sm font-medium">
                    Conversation will appear here
                  </p>
                  <p className="text-gray-400 text-xs mt-2">
                    Start speaking to begin the interview
                  </p>
                </div>
              ) : (
                messages.map((message, index) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex flex-col space-y-3"
                  >
                    <div className="flex items-center space-x-3">
                      {/* Clean Avatar */}
                      <div className={`relative p-1 w-8 h-8 rounded-2xl flex items-center justify-center ${
                        message.type === 'ai'
                          ? 'bg-amber-500/15 border border-amber-400/25'
                          : 'bg-green-500/15 border border-green-400/25'
                      }`}>
                        {message.type === 'ai' ? (
                          <img
                            src="/assets/images/branding/Website-Icon.png"
                            alt="MTAI Logo"
                            className="w-4 h-4 object-cover rounded"
                          />
                        ) : (
                          <User className="w-4 h-4 text-green-400" />
                        )}

                        {/* Fixed speaking indicator */}
                        {((message.type === 'ai' && isAiSpeaking) || (message.type === 'user' && isUserSpeaking && !isMuted)) && (
                          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse border-2 border-gray-900">
                            <div className="w-full h-full bg-green-500 rounded-full animate-ping opacity-75" />
                          </div>
                        )}
                      </div>
                      
                      {/* Name and timestamp */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-white text-sm truncate">
                            {message.type === 'ai' ? aiName : userName}
                          </span>
                          <span className="text-xs text-gray-400 ml-2 font-mono">
                            {formatTime(message.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Clean Message bubble */}
                    <div className={`ml-11 p-4 rounded-2xl ${
                      message.type === 'ai'
                        ? 'bg-white/8 border border-amber-400/10'
                        : 'bg-white/5 border border-white/8'
                    }`}>
                      <div className="text-sm leading-relaxed">
                        <TypewriterText
                          text={message.content}
                          isComplete={message.isComplete}
                          speed={15}
                        />

                        {/* Elegant blinking cursor for incomplete AI messages */}
                        {(!message.isComplete && message.type === 'ai') && (
                          <span className="inline-block ml-1 w-0.5 h-4 bg-amber-400 animate-pulse rounded-full" />
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))
              )}

              {/* Clean speaking indicator - Only show when NOT muted */}
              {isUserSpeaking && !isMuted && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center space-x-4 p-4 bg-white/5 border border-green-400/15 rounded-2xl"
                >
                  <div className="w-8 h-8 bg-green-500/15 rounded-2xl flex items-center justify-center border border-green-400/25">
                    <Mic className="w-4 h-4 text-green-400" />
                  </div>
                  <div className="flex-1">
                    <span className="text-green-400 text-sm font-semibold">You are speaking...</span>
                    <div className="flex space-x-1.5 mt-2">
                      {[0, 1, 2].map((i) => (
                        <div
                          key={i}
                          className="w-1.5 h-1.5 bg-green-400 rounded-full animate-bounce"
                          style={{ animationDelay: `${i * 0.2}s` }}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Clean footer */}
            <div className="border-t border-white/5 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 text-xs text-gray-400">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                  <span className="font-medium">
                    {messages.length} {messages.length === 1 ? 'message' : 'messages'}
                  </span>
                </div>

                {!autoScrollRef.current && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={() => {
                      autoScrollRef.current = true;
                      scrollToBottom();
                    }}
                    className="flex items-center space-x-2 px-3 py-2 bg-white/8 hover:bg-white/12 text-amber-400 text-xs rounded-xl transition-all duration-200 border border-amber-400/20"
                  >
                    <span className="text-sm">↓</span>
                    <span className="font-medium">Back to bottom</span>
                  </motion.button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
};

export default TranscriptionPanel;
